import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ChevronRight, KeyRound, Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useBranchTheme } from '@/lib/branchTheme';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// InvictaFit-Verlauf (Grün → Teal, passend zum Logo) für Buttons/Akzente
const BRAND_GRADIENT = 'linear-gradient(135deg, #22C55E 0%, #0F766E 100%)';

const DEFAULT_FEATURES = [
  'Online-Buchung & Kursplanung',
  'Kunden, Termine & Zahlungen an einem Ort',
  'Trainings- & Ernährungspläne mit KI',
  'Eigene App mit Ihrem Logo',
];
const DEFAULT_SUBTITLE =
  'Die Plattform für Studios, Praxen und Gesundheitsbetriebe — Buchung, Kundenverwaltung und Zahlungen an einem Ort.';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithToken, completeTotpLogin } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  // 2FA (Super-Admin): nach korrektem Passwort folgt ein TOTP-Schritt.
  const [totpPending, setTotpPending] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  // OTP State (Feb 2026 — passwortlos Login)
  const [mode, setMode] = useState('otp_request');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpDelivery, setOtpDelivery] = useState(null);
  const { logoUrl, name: brandName, ready } = useTenantBranding();
  const { branch, loading: branchLoading } = useBranchTheme();

  // Branchen-Theme (Logo, Text, Farben) – Fallback: InvictaFit-Branding.
  const accent = branch?.accent_color || '#16A34A';
  const GRAD = branch?.accent_gradient || BRAND_GRADIENT;
  const panelLogo = branch ? branch.logo_url : logoUrl;
  const panelName = branch?.name || brandName;
  // Logo erst zeigen, wenn Branding & Branche aufgelöst sind → kein Flash des alten Defaults.
  const brandingResolved = ready && !branchLoading;
  const panelSubtitle = branch?.subtitle || branch?.description || DEFAULT_SUBTITLE;
  const panelFeatures = (branch?.features || DEFAULT_FEATURES).slice(0, 4);
  const branchQuery = branch ? `?branch=${branch.key}` : '';

  const routeByRole = (user) => {
    // QR-Deeplink / geschützte Zielseite: nach Login dorthin zurückkehren
    // (z. B. /workshop?vehicle=<id> → öffnet direkt die Fahrzeug-Historie).
    const from = location.state?.from;
    if (from && from.pathname && from.pathname !== '/login') {
      navigate((from.pathname || '/') + (from.search || ''), { replace: true });
      return;
    }
    if (user.role === 'mieter') navigate('/resources');
    else if (user.role === 'trainer') navigate('/appointments');
    else if (user.role === 'customer') navigate('/portal');
    else if (user.role === 'superadmin') navigate('/superadmin');
    else navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result && result.requires_totp) {
        setTotpPending(result.pending_token);
        setTotpCode('');
        setMode('totp');
        toast.message('Bitte Bestätigungscode eingeben');
        return;
      }
      toast.success('Willkommen zurück!');
      routeByRole(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await completeTotpLogin(totpPending, totpCode.trim());
      toast.success('Willkommen zurück!');
      routeByRole(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code ungültig');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/otp/request`, {
        email: otpEmail.trim().toLowerCase(),
      });
      setOtpDelivery(data?.delivered_via || {});
      setMode('otp_verify');
      const channels = [];
      if (data?.delivered_via?.email) channels.push('E-Mail');
      if (data?.delivered_via?.whatsapp) channels.push('WhatsApp');
      toast.success(
        channels.length
          ? `Code wurde via ${channels.join(' + ')} gesendet`
          : 'Code erstellt – bitte prüfe deine Nachrichten'
      );
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        toast.error('Zu viele Anfragen. Bitte warte 10 Minuten.');
      } else {
        toast.error(error.response?.data?.detail || 'Code-Versand fehlgeschlagen');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/otp/verify`, {
        identifier: otpEmail.trim().toLowerCase(),
        code: otpCode.trim(),
      });
      if (loginWithToken) {
        await loginWithToken(data.access_token, data.refresh_token);
      } else {
        localStorage.setItem('token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
      }
      toast.success('Willkommen!');
      const from = location.state?.from;
      if (from && from.pathname && from.pathname !== '/login') {
        navigate((from.pathname || '/') + (from.search || ''), { replace: true });
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code ungültig');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left Side - Branchen-Branding (hell, invictaflow.ch-Stil) */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent}12 0%, #FFFFFF 55%, #F8FAFC 100%)` }}
      >
        {/* Branchen-Glow */}
        <div
          className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-40"
          style={{ background: `radial-gradient(circle, ${accent}2e, transparent 70%)` }}
        />
        <div
          className="absolute -bottom-32 -left-20 w-[380px] h-[380px] rounded-full opacity-30"
          style={{ background: `radial-gradient(circle, ${accent}24, transparent 70%)` }}
        />

        <div className="relative z-10 max-w-md animate-fade-up">
          <div className="w-44 h-44 mb-6 flex items-center justify-center">
            {brandingResolved && (
              <img
                src={panelLogo}
                alt={panelName}
                data-testid="login-brand-logo"
                className="w-44 h-44 object-contain drop-shadow-sm animate-fade-up"
              />
            )}
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold leading-tight text-slate-900"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Weniger Büroarbeit.<br />
            <span style={{ color: accent }}>Mehr Zeit für Ihre Kunden.</span>
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {panelSubtitle}
          </p>
          <div className="mt-10 space-y-4 stagger-children">
            {panelFeatures.map((text, i) => (
              <div key={i} className="flex items-center gap-4 group">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center border transition-colors"
                  style={{ borderColor: `${accent}40`, backgroundColor: `${accent}12` }}
                >
                  <ChevronRight size={18} style={{ color: accent }} />
                </div>
                <span className="text-slate-700 text-sm font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-slate-50">
        {/* Mobile Logo */}
        <div className="lg:hidden absolute top-6 left-4 flex items-center gap-2">
          {brandingResolved && (
            <img src={panelLogo} alt={panelName} className="h-10 w-auto object-contain" />
          )}
        </div>

        <Card className="w-full max-w-md mt-16 lg:mt-0 border-slate-200/70 shadow-xl animate-fade-up" data-testid="login-card">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl sm:text-3xl text-slate-900" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {mode === 'password' ? 'ADMIN-LOGIN' : 'ANMELDEN'}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              {mode === 'password' && 'Anmeldung mit E-Mail und Passwort (nur Admin)'}
              {mode === 'otp_request' && 'Gib deine E-Mail ein — wir senden dir einen Login-Code'}
              {mode === 'otp_verify' && '6-stelligen Code aus E-Mail/WhatsApp eingeben'}
              {mode === 'totp' && 'Zweiter Faktor — Bestätigungscode eingeben'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Passwort-Login */}
            {mode === 'password' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">E-Mail</Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="name@beispiel.ch"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Passwort</Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg font-bold uppercase tracking-wide text-white transition-all active:scale-[0.97]"
                  style={{ background: GRAD }}
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Wird angemeldet...' : 'Anmelden'}
                </Button>
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-400">oder</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 rounded-lg font-semibold gap-2"
                  onClick={() => {
                    setOtpEmail(formData.email);
                    setMode('otp_request');
                  }}
                  data-testid="login-otp-switch-button"
                >
                  <KeyRound size={16} />
                  Per Code anmelden (ohne Passwort)
                </Button>
              </form>
            )}

            {/* 2FA (Super-Admin) — zweiter Faktor */}
            {mode === 'totp' && (
              <form onSubmit={handleTotpSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="totp-code" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bestätigungscode</Label>
                  <Input
                    id="totp-code"
                    data-testid="login-totp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    placeholder="6-stelliger Code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    required
                    className="h-11 rounded-lg border-slate-200 text-center tracking-[0.3em] focus:border-slate-400 focus:ring-slate-200"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Code aus deiner Authenticator-App. Alternativ einen deiner Wiederherstellungs-Codes eingeben.
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg font-bold uppercase tracking-wide text-white transition-all active:scale-[0.97]"
                  style={{ background: GRAD }}
                  disabled={loading || !totpCode}
                  data-testid="login-totp-submit-button"
                >
                  {loading ? 'Wird geprüft...' : 'Bestätigen'}
                </Button>
                <button
                  type="button"
                  className="w-full text-xs text-slate-400 hover:text-slate-600 pt-2 transition-colors"
                  onClick={() => { setMode('password'); setTotpPending(null); setTotpCode(''); }}
                  data-testid="login-totp-cancel"
                >
                  ← Zurück
                </button>
              </form>
            )}

            {/* OTP Request */}
            {mode === 'otp_request' && (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">E-Mail</Label>
                  <Input
                    id="otp-email"
                    data-testid="otp-email-input"
                    type="email"
                    placeholder="name@beispiel.ch"
                    value={otpEmail}
                    onChange={(e) => setOtpEmail(e.target.value)}
                    required
                    autoFocus
                    className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Wir senden dir gleichzeitig einen Code per E-Mail und WhatsApp (sofern in deinem Profil hinterlegt).
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg font-bold uppercase tracking-wide gap-2 text-white"
                  style={{ background: GRAD }}
                  disabled={loading || !otpEmail}
                  data-testid="otp-request-button"
                >
                  <Mail size={16} />
                  {loading ? 'Wird gesendet...' : 'Code senden'}
                </Button>
                <button
                  type="button"
                  className="w-full text-xs text-slate-400 hover:text-slate-600 pt-2 transition-colors"
                  onClick={() => setMode('password')}
                  data-testid="admin-password-login-link"
                >
                  Admin-Login mit Passwort →
                </button>
              </form>
            )}

            {/* OTP Verify */}
            {mode === 'otp_verify' && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
                  Code wurde gesendet an <strong>{otpEmail}</strong>
                  {otpDelivery && (
                    <div className="mt-1 text-slate-500">
                      Kanäle: {otpDelivery.email ? '📧 E-Mail' : ''} {otpDelivery.whatsapp ? '💬 WhatsApp' : ''}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp-code" className="text-xs font-semibold uppercase tracking-wider text-slate-500">6-stelliger Code</Label>
                  <Input
                    id="otp-code"
                    data-testid="otp-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="••••••"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    required
                    autoFocus
                    className="h-14 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200 text-center text-2xl tracking-[0.6em] font-bold"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg font-bold uppercase tracking-wide text-white"
                  style={{ background: GRAD }}
                  disabled={loading || otpCode.length !== 6}
                  data-testid="otp-verify-button"
                >
                  {loading ? 'Wird geprüft...' : 'Code bestätigen'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-10 text-slate-500 gap-2"
                  onClick={() => { setMode('otp_request'); setOtpCode(''); }}
                >
                  <ArrowLeft size={14} /> Code erneut anfordern
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm">
              <span className="text-slate-500">Noch kein Konto? </span>
              <Link to={`/register${branchQuery}`} className="font-semibold hover:opacity-80" style={{ color: accent }} data-testid="register-link">
                Jetzt registrieren
              </Link>
            </div>
            <div className="mt-2 text-center text-sm">
              <Link to="/branchen" className="text-slate-400 hover:text-slate-600 font-medium" data-testid="branches-link">
                Alle Branchen ansehen →
              </Link>
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4 text-center text-sm">
              <span className="text-slate-500">Eigenes Studio/Praxis? </span>
              <Link to="/betrieb-registrieren" className="font-semibold hover:opacity-80" style={{ color: accent }} data-testid="business-register-link">
                Betrieb registrieren
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
